import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { fetchSingleWorkflow } from '../shared/redtailQueryHelpers'
import { applyTaskToggle, applyStepComplete } from './workflowOptimistic'

function workflowDetailKey (workflowId) {
  return [QUERY_KEYS.redtailDomainQuery, 'workflow-detail', workflowId]
}

export { workflowDetailKey }

export default function useWorkflowDetail (workflowId) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: workflowDetailKey(workflowId),
    queryFn: () => fetchSingleWorkflow(workflowId, clientId),
    staleTime: 30_000,
    enabled: !!workflowId
  })

  const workflow = query.data ?? null

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: workflowDetailKey(workflowId)
    })
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
    })
  }, [queryClient, workflowId])

  const { mutateAsync: toggleTaskMutation } = useMutation({
    mutationFn: ({ stepId, taskId, wasComplete }) => {
      const command = wasComplete ? 'uncompleteWorkflowTask' : 'completeWorkflowTask'
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command,
        autoResolveConnection: true,
        clientId,
        request: { workflowId, stepId, taskId }
      })
    },
    onSuccess: (_data, { stepId, taskId, wasComplete }) => {
      queryClient.setQueryData(
        workflowDetailKey(workflowId),
        (old) => applyTaskToggle(old, stepId, taskId, wasComplete)
      )
      invalidate()
    }
  })

  const { mutateAsync: completeStepMutation } = useMutation({
    mutationFn: ({ stepId, outcomeId, notes }) => {
      const request = { workflowId, stepId }
      if (outcomeId) request.outcome_id = outcomeId
      if (notes) request.step_notes = notes
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'completeWorkflowStep',
        autoResolveConnection: true,
        clientId,
        request
      })
    },
    onSuccess: (_data, { stepId, nextStepId }) => {
      queryClient.setQueryData(
        workflowDetailKey(workflowId),
        (old) => applyStepComplete(old, stepId, nextStepId)
      )
      invalidate()
    }
  })

  return {
    workflow,
    isLoading: query.isLoading,
    toggleTask: (stepId, taskId, wasComplete) => toggleTaskMutation({ stepId, taskId, wasComplete }),
    completeStep: (stepId, outcomeId, notes, nextStepId) => completeStepMutation({ stepId, outcomeId, notes, nextStepId })
  }
}
